/* eslint-disable lines-around-comment */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ResourceValueType } from './Resource.enum';
import { getResources } from '../../../../app/services/enterpriseService';

/**
 * Object lookup with permissions per resource.
 * Key is the resource id and the value is
 * the sum of all policy type or action values.
 *
 * @example
 *  // policy CREATE 0001
 *  // policy READ 0010
 *  // policy WRITE = 0100
 *
 *  {
 *    1: 0101, // CREATE + WRITE
 *    2: 0111, // CREATE + READ + WRITE
 *    3: 0011, // CREATE + READ
 *  }
 * @export
 * @interface AccessControlPermissions
 */
export type AccessControlPermissions = {
    [key in ResourceValueType]?: number;
};

/**
 * Redux store for permissions for current company.
 *
 * @export
 * @interface PermissionsState
 */
export interface PermissionsState {
    /**
     * Stores the main identifier for the permissions.
     *
     * In example the RUT of a company 70360100 (from 70.360.100-6)
     *
     * @type {string}
     */
    entity?: string;

    /**
     * Whether permissions are loading.
     *
     * @type {boolean}
     */
    loading: boolean;

    /**
     * Object lookup with the allowed actions
     * or policy types for each resource.
     *
     * @type {AccessControlPermissions}
     */
    permissions: AccessControlPermissions;

    sideBarInfo: {
        empresaYGestion: boolean;
    };
}

const getPermissionsFromLocalStorage = () => {
    const permissions = localStorage.getItem('dataRecursos');

    if (!permissions) return null;

    return JSON.parse(permissions).data;
};

export const fetchPermissions = createAsyncThunk(
    'session/permissions',
    async (entity: string) => {
        let responseResources = getPermissionsFromLocalStorage();

        if (!responseResources) {
            const tokenRoles: string =
                localStorage.getItem('Authorization')?.split(' ')[1] || '';
            responseResources = await getResources(tokenRoles);
        }

        const enterprises = responseResources.data.map((ent: any) => {
            return {
                rut: ent.entidad,
                resources: ent.rolRecursos
            };
        });

        return {
            entity,
            permissions: enterprises
        };
    }
);

/**
 * Redux slice with actions and reducers.
 *
 * @example
 *  import { permissionSlice } from '@ui-core'
 *
 *  const rootReducer = combineReducers({
 *    ...,
 *    [permissionSlice.name]: permissionSlice.reducer,
 *    ...,
 *  })
 *
 * ---
 *
 *  // for dispatch in some component or middleware
 *  import { useDispatch } from 'react-redux'
 *  import { fetchPermissions } from '@ui-core'
 *
 *  export const SomeComponent = () => {
 *    const dispatch = useDispatch()
 *
 *    useEffect(() => {
 *      dispatch(fetchPermissions('12312312-3'))
 *    }, [dispatch])
 *  ...
 *  }
 * @export
 * @returns {Slice}
 */
export const permissionSlice = createSlice({
    name: 'permissions',
    initialState: {
        loading: false,
        permissions: [],
        sideBarInfo: {
            empresaYGestion: false
        }
    } as PermissionsState,
    reducers: {
        getSideBarInfo(state, action: PayloadAction<any>) {
            state.sideBarInfo = action.payload;
        }
    },
    extraReducers: {
        [fetchPermissions.pending.type]: (state) => {
            state.loading = true;
        },
        [fetchPermissions.fulfilled.type]: (
            state,
            { payload }: PayloadAction<Omit<PermissionsState, 'loading'>>
        ) => {
            state.loading = false;
            state.entity = payload.entity;
            state.permissions = payload.permissions;
        },
        [fetchPermissions.rejected.type]: (state) => {
            state.loading = false;
        }
    }
});

export interface PermissionsRootStore {
    [permissionSlice.name]: PermissionsState;
}
